import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, Pencil, Plus, Upload } from "lucide-react";
import { toast } from "sonner";

import DashboardHeader from "../components/DashboardHeader";
import InventoryRecordDialog from "../components/InventoryRecordDialog";

const api = `${process.env.REACT_APP_BACKEND_URL}/api/inventory`;

const summaryCards = (summary) => [
  ["Total units", summary.total_units],
  ["Unique records", summary.unique_records],
  ["Total cost basis", `$${summary.total_cost_basis}`],
  ["Manual market value", `$${summary.estimated_inventory_value}`],
  ["Unlisted units", summary.unlisted_units],
  ["Low-stock records", summary.low_stock_records],
];

export default function InventoryPage({ user, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const [summaryResponse, listResponse] = await Promise.all([
      axios.get(`${api}/summary`, { withCredentials: true }),
      axios.get(api, { withCredentials: true, params: { search, limit: 50 } }),
    ]);
    setSummary(summaryResponse.data);
    setItems(listResponse.data.items);
  }, [search]);

  useEffect(() => {
    load().catch(() => toast.error("Cardfolio couldn’t load inventory."));
  }, [load]);

  const save = async (payload) => {
    if (selected) {
      await axios.patch(
        `${api}/${selected.item_id}`,
        { ...payload, version: selected.version },
        { withCredentials: true },
      );
      toast.success("Inventory record updated.");
    } else {
      await axios.post(api, payload, { withCredentials: true });
      toast.success("Inventory record saved.");
    }
    setSelected(null);
    await load();
  };

  const exportItems = async () => {
    try {
      const response = await axios.post(
        `${api}/export`,
        { item_ids: items.map((item) => item.item_id), action: "export" },
        { withCredentials: true, responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "cardfolio-inventory.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Cardfolio couldn’t export inventory. Please try again.");
    }
  };

  const openCreate = () => {
    setSelected(null);
    setEditorOpen(true);
  };

  const openEdit = (item) => {
    setSelected(item);
    setEditorOpen(true);
  };

  if (!summary) {
    return (
      <main className="loading-screen" data-testid="inventory-loading-state">
        Loading inventory
      </main>
    );
  }

  return (
    <div
      className="dashboard-shell workspace-shell"
      data-testid="inventory-workspace"
    >
      <DashboardHeader user={user} onLogout={onLogout} activePage="Inventory" />
      <main className="inventory-main">
        <header className="inventory-page-header">
          <div>
            <p className="eyebrow">Business inventory</p>
            <h1>Inventory</h1>
            <p>
              Track each card’s quantity, condition, cost, manual value, and
              storage.
            </p>
          </div>
          <div className="inventory-header-actions">
            <button
              className="admin-secondary-button"
              type="button"
              onClick={() =>
                toast.message(
                  "Spreadsheet and scanner imports are planned next.",
                )
              }
              data-testid="import-inventory-button"
            >
              <Upload size={16} /> Import inventory
            </button>
            <button
              className="admin-primary-button"
              type="button"
              onClick={openCreate}
              data-testid="add-inventory-button"
            >
              <Plus size={17} /> Add inventory
            </button>
          </div>
        </header>

        <section className="inventory-summary" data-testid="inventory-summary">
          {summaryCards(summary).map(([label, value]) => (
            <article
              key={label}
              data-testid={`inventory-summary-${label.toLowerCase().replaceAll(" ", "-")}`}
            >
              <p>{label}</p>
              <strong>{value}</strong>
            </article>
          ))}
        </section>

        <section className="inventory-table-section">
          <div className="inventory-toolbar">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search cards, sets, SKU, storage…"
              data-testid="inventory-search-input"
            />
            <button
              className="inventory-export-button"
              type="button"
              onClick={exportItems}
              disabled={!items.length}
              data-testid="export-inventory-button"
            >
              <Download size={15} /> Export visible
            </button>
          </div>

          {items.length ? (
            <div className="inventory-table-wrap">
              <table className="inventory-table" data-testid="inventory-table">
                <thead>
                  <tr>
                    <th>Card</th>
                    <th>Game</th>
                    <th>Condition</th>
                    <th>Qty</th>
                    <th>Unit cost</th>
                    <th>Manual value</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      data-testid={`inventory-row-${item.item_id}`}
                      key={item.item_id}
                    >
                      <td>
                        <button
                          className="inventory-card-link"
                          onClick={() => openEdit(item)}
                          type="button"
                          data-testid={`open-inventory-${item.item_id}`}
                        >
                          {item.card_name}
                        </button>
                        <span>
                          {item.set_name || "No set"}
                          {item.set_code ? ` · ${item.set_code}` : ""}
                        </span>
                      </td>
                      <td>{item.game}</td>
                      <td>{item.condition}</td>
                      <td>{item.quantity}</td>
                      <td>${item.unit_cost}</td>
                      <td>${item.market_value}</td>
                      <td>
                        <span className="status-badge status-active">
                          {item.inventory_status}
                        </span>
                      </td>
                      <td>{item.storage?.custom_label || "Unassigned"}</td>
                      <td>{new Date(item.updated_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="inventory-edit-button"
                          onClick={() => openEdit(item)}
                          type="button"
                          data-testid={`edit-inventory-${item.item_id}`}
                        >
                          <Pencil size={14} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="inventory-empty"
              data-testid="inventory-empty-state"
            >
              <h2>Your inventory starts here</h2>
              <p>
                Add your first card to begin tracking quantities, costs, values,
                and storage.
              </p>
              <button
                className="admin-primary-button"
                type="button"
                onClick={openCreate}
                data-testid="empty-add-inventory-button"
              >
                Add inventory
              </button>
            </div>
          )}
        </section>
      </main>
      <InventoryRecordDialog
        open={editorOpen}
        onOpenChange={(value) => {
          setEditorOpen(value);
          if (!value) {
            setSelected(null);
          }
        }}
        item={selected}
        onSave={save}
      />
    </div>
  );
}
